import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import {autoTable, RowInput} from 'jspdf-autotable';
import {EntityInfo} from '../../../../../backend/interfaces/entity-info';
import {User} from '../../../../../backend/interfaces/user';
import {pdfFooterInfo} from '../../base/export-pdf-footer-info';
import {pdfHeaderInfo} from '../../base/export-pdf-header-info';
import {translations} from '../../../../../i18n/translations';
import {buildExportFileName} from '../../base/export-file-name';
import type {PreparedPdfExportFile} from '../../base/export-data-types';
import {exportCarePlanActivitiesGroupPdfData} from './export-care-plan-group-pdf-data';
import {ExportCarePlanReportData} from './export-care-plan-types';
import {PDF_FONT_FAMILY, PDF_FONT_MAIN_COLOR, registerPdfFonts} from '../../base/export-pdf-font';
import {createAutoTableRichTextHooks} from '../../base/export-pdf-rich-text-autotable';

export async function prepareExportCarePlanPDF({
  reportData,
  senior,
  entity,
  t,
}: {
  reportData: ExportCarePlanReportData;
  entity: EntityInfo;
  senior: User;
  t: TFunction;
}): Promise<PreparedPdfExportFile> {
  const pdf = new jsPDF({orientation: 'landscape', compress: true});
  await registerPdfFonts(pdf);

  const seniorName = senior?.displayName ?? senior?.name ?? 'n/a';
  const tableData = await exportCarePlanActivitiesGroupPdfData(reportData, t);

  pdf.setProperties({
    title: `${seniorName} - ${t(translations.screen_paths.senior_care_plan)}`,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  // --- Main title ---
  if (tableData.title) {
    // pdf.setTextColor('#2a80ba');
    pdf.setTextColor(PDF_FONT_MAIN_COLOR);
    pdf.setFont(PDF_FONT_FAMILY, 'bold');
    pdf.setFontSize(14);
    pdf.text(tableData.title, 15, 28);
  }

  // --- Details title ---
  if (tableData.details.headers.length > 0) {
    if (tableData.details.title) {
      pdf.setTextColor(PDF_FONT_MAIN_COLOR);
      pdf.setFont(PDF_FONT_FAMILY, 'bold');
      pdf.setFontSize(12);
      pdf.text(tableData.details.title, 16, 36);
    }

    // --- Details table ---
    const detailsTableHeader: string[] = tableData.details.headers;
    const detailsRows: RowInput[] = tableData.details.rows;

    // create table
    autoTable(pdf, {
      head: [detailsTableHeader],
      body: detailsRows,
      margin: {top: tableData.details.title ? 38 : 36},
      styles: {font: PDF_FONT_FAMILY, fontSize: 9, valign: 'middle'},
      headStyles: {font: PDF_FONT_FAMILY, fontStyle: 'bold'},
      columnStyles: tableData.details.columnStyles,
      tableWidth: 'auto',
      rowPageBreak: 'avoid',
    });
  }

  // --- Compute dynamic task YY after details table ---
  const detailsTableFinalY: number = (pdf as any).lastAutoTable?.finalY ?? 28;
  const tasksTitleY = detailsTableFinalY + 8;
  const tasksTableY = tableData.tasks.title ? tasksTitleY + 2 : tasksTitleY;

  // --- Tasks title ---
  if (tableData.tasks.title) {
    pdf.setTextColor(PDF_FONT_MAIN_COLOR);
    pdf.setFont(PDF_FONT_FAMILY, 'bold');
    pdf.setFontSize(12);
    pdf.text(tableData.tasks.title, 16, tasksTitleY);
  }

  // --- Tasks table ---
  const tasksTableHeader = tableData.tasks.headers;
  const tasksRows: RowInput[] = tableData.tasks.rows;
  const taskRichTextHooks = createAutoTableRichTextHooks({pdf});

  // create table
  autoTable(pdf, {
    head: [tasksTableHeader],
    body: tasksRows,
    startY: tasksTableY,
    columnStyles: tableData.tasks.columnStyles,
    styles: {font: PDF_FONT_FAMILY, fontSize: 9, valign: 'middle'},
    headStyles: {font: PDF_FONT_FAMILY, fontStyle: 'bold'},
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    willDrawPage: function (data) {
      // ---- Add Header to first page ----
      if (data.pageNumber === 1) {
        pdfHeaderInfo(pdf, senior, entity, t);

        // Reset custom margins for the rest of the pages
        data.settings.margin.top = 7;
        data.settings.margin.bottom = 15;
      }
    },
    didDrawPage: function (data) {
      // ---- Add Footer to all pages ----
      const pageCounter = data.pageNumber.toString();
      pdfFooterInfo(pdf, pageCounter);
    },
    didParseCell: (data) => {
      taskRichTextHooks.didParseCell(data);
    },
    willDrawCell: (data) => {
      taskRichTextHooks.willDrawCell(data);
    },
    didDrawCell: (data) => {
      taskRichTextHooks.didDrawCell(data);
    },
  });

  const fileName = buildExportFileName([
    senior.displayName ?? senior.name,
    t(translations.export.plan),
    reportData.sectorData.name,
    reportData.groupData.name,
  ]);

  // --- Done ---
  return {
    file: pdf,
    fileName,
  };
}
